/**
 * AndroidBridge
 * Android WebView 平台的 Bridge 實作
 */

import { BridgeStrategy } from './BridgeStrategy'
import { BridgeAction } from '../types/bridge.types'
import type { PlatformType } from '../types/platform.types'

export class AndroidBridge extends BridgeStrategy {
  getPlatform(): PlatformType {
    return 'android'
  }

  async connect(): Promise<void> {
    try {
      this.updateConnectionStatus('connecting')

      // 等待 Android bridge 注入（最多等待 2 秒）
      const bridgeReady = await this.waitForBridgeInjection()

      if (!bridgeReady) {
        throw new Error(
          'Android bridge not available after waiting. The WebView may not have injected the bridge object.'
        )
      }

      this.setupListeners()

      this.updateConnectionStatus('connected')
      this.updateLastActivity()
    } catch (error) {
      this.updateConnectionStatus('error')
      console.error('Failed to connect Android bridge:', error)
      throw error
    }
  }

  /**
   * 等待 Android bridge 注入
   * 某些 Android WebView 可能會延遲注入 bridge 物件
   */
  private async waitForBridgeInjection(
    maxWaitTime: number = 2000
  ): Promise<boolean> {
    const startTime = Date.now()
    const checkInterval = 100 // 每 100ms 檢查一次

    while (Date.now() - startTime < maxWaitTime) {
      // 檢查 Android 或 asyncBridge 是否可用
      if (this.win.Android || this.win.asyncBridge) {
        // 進一步檢查是否有可用的方法
        try {
          if (this.win.Android) {
            // 檢查至少一個方法是否可用
            if (
              ('getStudentList' in this.win.Android &&
                typeof this.win.Android.getStudentList === 'function') ||
              ('receiveMessage' in this.win.Android &&
                typeof this.win.Android.receiveMessage === 'function')
            ) {
              return true
            }
          }

          if (this.win.asyncBridge) {
            // 檢查 asyncBridge 方法
            if (
              'getStudentList' in this.win.asyncBridge &&
              typeof this.win.asyncBridge.getStudentList === 'function'
            ) {
              return true
            }
          }
        } catch (e) {
          // 忽略檢查過程中的錯誤
        }
      }

      // 等待下一次檢查
      await new Promise(resolve => setTimeout(resolve, checkInterval))
    }

    console.warn(`Android bridge not available after waiting ${maxWaitTime}ms`)
    return false
  }

  disconnect(): void {
    this.updateConnectionStatus('disconnected')
    this.cleanup()
  }

  setupListeners(): void {
    // Android 通常使用直接方法呼叫，不需要設置監聽器
    // 但可能需要處理來自原生端的回調

    // 註冊全域回調函式供 Android 呼叫
    interface WindowWithAndroidCallback extends Window {
      onAndroidMessage?: (messageStr: string) => void
    }
    
    (window as WindowWithAndroidCallback).onAndroidMessage = (messageStr: string) => {
      try {
        const data = JSON.parse(messageStr)
        this.handleMessage(data)
      } catch (error) {
        console.error('Error handling Android message:', error)
      }
    }
  }

  /**
   * 發送訊息到 Android 原生端（實作抽象方法）
   */
  async sendMessage<T>(action: BridgeAction, data: unknown): Promise<T> {
    // 優先使用 Android 物件方法
    if (this.win.Android) {
      const androidBridge = this.win.Android as Record<string, unknown>
      const actionName = action as string
      if (typeof androidBridge[actionName] === 'function') {
        try {
          const fn = androidBridge[actionName] as (data: string) => unknown
          const result = fn(JSON.stringify(data))
          
          this.updateLastActivity()
          return this.parseResponse<T>(result)
        } catch (error) {
          const err = error as Error
          console.error(`Android.${actionName} failed:`, err)
          // 如果是注入錯誤，不繼續嘗試其他方法
          if (err.message && err.message.includes('non-injected object')) {
            throw new Error(
              `Android WebView injection error: ${err.message}. The bridge object may not be properly initialized.`
            )
          }
        }
      }
    }

    // 使用 AsyncBridge
    if (this.win.asyncBridge) {
      const actionName = action as string
      try {
        // 檢查方法是否真的存在並且是函數
        if (typeof this.win.asyncBridge[actionName] === 'function') {
          const result = await this.callAsyncBridge(actionName, data)
          this.updateLastActivity()
          return this.parseResponse<T>(result)
        } else {
          console.warn(`asyncBridge.${actionName} is not a function`)
        }
      } catch (error) {
        const err = error as Error
        console.error(`AsyncBridge ${actionName} failed:`, err)
        // 如果是注入錯誤，拋出更明確的錯誤
        if (
          err.message &&
          (err.message.includes('non-injected object') ||
            err.message.includes('not found'))
        ) {
          throw error
        }
      }
    }

    // 使用 receiveMessage
    if (
      this.win.Android &&
      typeof this.win.Android.receiveMessage === 'function'
    ) {
      try {
        const message = this.createMessage(action, data)
        const promise = this.createPendingRequest<T>(message.messageId!, 5000)

        this.win.Android.receiveMessage(JSON.stringify(message))

        const result = await promise
        this.updateLastActivity()
        return result
      } catch (error) {
        console.error('Android.receiveMessage failed:', error)
      }
    }

    // 如果所有方法都失敗，提供詳細的錯誤信息
    const errorMsg = this.generateBridgeErrorMessage()
    throw new Error(errorMsg)
  }

  /**
   * 生成詳細的錯誤信息
   */
  private generateBridgeErrorMessage(): string {
    const parts: string[] = ['No Android bridge method available.']

    if (!this.win.Android && !this.win.asyncBridge) {
      parts.push('Neither Android nor asyncBridge objects are injected.')
      parts.push(
        'Please ensure the Android WebView has properly injected the JavaScript bridge.'
      )
    } else {
      if (this.win.Android) {
        const methods = Object.keys(this.win.Android).filter(
          key => typeof (this.win.Android as any)[key] === 'function'
        )
        parts.push(
          `Android object exists with methods: ${methods.join(', ') || 'none'}`
        )
      }

      if (this.win.asyncBridge) {
        const methods = Object.keys(this.win.asyncBridge).filter(
          key => typeof this.win.asyncBridge![key] === 'function'
        )
        parts.push(
          `asyncBridge object exists with methods: ${methods.join(', ') || 'none'}`
        )
      }
    }

    return parts.join(' ')
  }
}
